import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Check, Star } from 'lucide-react'

const PricingPreview = () => {
  const [calculatorInputs, setCalculatorInputs] = useState({
    candidates: 50,
    traditionalCost: 300,
    aiCost: 49
  })

  const [results, setResults] = useState({
    traditionalSpend: 0,
    aiSpend: 0,
    savings: 0
  })

  useEffect(() => {
    const traditionalSpend = calculatorInputs.candidates * calculatorInputs.traditionalCost
    const aiSpend = calculatorInputs.candidates * calculatorInputs.aiCost
    const savings = traditionalSpend - aiSpend

    setResults({ traditionalSpend, aiSpend, savings })
  }, [calculatorInputs])

  const plans = [
    {
      name: "Free Trial",
      price: "$0",
      period: "/ month",
      description: "Perfect for testing BudgetHyre's AI interviewing",
      features: [
        "5 interviews per month",
        "Basic summaries",
        "Email invites",
        "Community support"
      ]
    },
    {
      name: "Startup",
      price: "$2.50",
      period: "/ interview",
      description: "Budget-friendly for early-stage startups",
      featured: true,
      features: [
        "Unlimited interviews",
        "Detailed insights & reports",
        "Custom question templates",
        "Priority email support"
      ]
    },
    {
      name: "Scale",
      price: "$99",
      period: "/ 50 interviews",
      description: "For growing teams scaling fast",
      features: [
        "Volume pricing (50% savings)",
        "Advanced analytics dashboard",
        "Team collaboration tools",
        "Slack/Teams integration"
      ]
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For organizations with custom needs",
      features: [
        "Volume commitments & discounts",
        "White-label solution",
        "API access & integrations",
        "Dedicated success manager"
      ]
    }
  ]

  const handleInputChange = (field, value) => {
    setCalculatorInputs(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }))
  }

  return (
    <section id="pricing" className="relative section bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-primary-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-20 right-1/4 w-48 h-48 bg-accent-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-float" />
      </div>

      <div className="container relative z-10">
        <motion.div 
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-50 to-accent-50 rounded-full border border-primary-200/50 text-primary-700 text-sm font-medium mb-6">
            <Star className="w-4 h-4" />
            <span>Budget-Friendly Pricing</span>
          </div>
          <h2 className="text-responsive-xl font-black text-gray-900 mb-4">
            <span className="text-gradient">Transparent Pricing</span>
            <br />Built for Startups
          </h2>
          <p className="text-responsive-lg text-gray-600 max-w-2xl mx-auto">
            Pay-as-you-go with no monthly commitments. Scale your hiring budget smartly.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-16 lg:mb-20">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${
                plan.featured 
                  ? 'lg:scale-110 lg:-translate-y-4' 
                  : ''
              }`}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: plan.featured ? 1.1 : 1 }}
              transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{ scale: plan.featured ? 1.15 : 1.05, y: -8 }}
            >
              <div className={`bg-white border-2 rounded-2xl p-6 lg:p-8 h-full relative shadow-lg hover:shadow-xl transition-all duration-300 ${
                plan.featured 
                  ? 'border-blue-500 ring-2 ring-blue-100' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      <Star className="w-3 h-3" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-3">
                    <span className="text-4xl font-bold text-gray-900">
                      {plan.price}
                    </span>
                    <span className="text-gray-500 text-sm ml-1">{plan.period}</span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-green-600" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button 
                  className={`w-full py-3 px-6 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    plan.featured
                      ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-md hover:shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {plan.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div 
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Link 
            to="/pricing" 
            className="btn btn-outline text-base px-8 py-4 group"
          >
            See Detailed Pricing
            <motion.div
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="ml-2"
            >
              →
            </motion.div>
          </Link>
        </motion.div>

        {/* Calculator */}
        <motion.div
          className="max-w-4xl mx-auto glass-card backdrop-blur-xl p-8 lg:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
              Calculate Your <span className="text-gradient">Savings</span>
            </h3>
            <p className="text-gray-600">See how much BudgetHyre can save your startup</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              { label: 'Monthly Candidates', field: 'candidates', value: calculatorInputs.candidates },
              { label: 'Traditional Cost per Screen ($)', field: 'traditionalCost', value: calculatorInputs.traditionalCost },
              { label: 'BudgetHyre Cost per Interview ($)', field: 'aiCost', value: calculatorInputs.aiCost }
            ].map((input, index) => (
              <motion.div
                key={input.field}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {input.label}
                </label>
                <input
                  type="number"
                  value={input.value}
                  onChange={(e) => handleInputChange(input.field, e.target.value)}
                  className="focus-modern w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-900 font-semibold transition-all duration-200"
                  min="0"
                />
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 lg:p-8 glass-card-dark rounded-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            {[
              { label: 'Traditional Spend', value: results.traditionalSpend, color: 'text-red-400' },
              { label: 'BudgetHyre Spend', value: results.aiSpend, color: 'text-blue-400' },
              { label: 'Your Savings', value: results.savings, color: 'text-green-400' }
            ].map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-sm text-gray-400 mb-2 font-medium">{result.label}</div>
                <div className={`text-2xl lg:text-3xl font-black ${result.color}`}>
                  ${result.value.toLocaleString()}
                </div>
                {index === 2 && result.value > 0 && (
                  <div className="text-xs text-green-400 mt-1">
                    {Math.round((result.value / results.traditionalSpend) * 100)}% savings
                  </div>
                )}
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default PricingPreview
